import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  Users,
  Heart,
  Baby,
  Crown,
  Link as LinkIcon,
  Apple,
  Bot,
  AlertCircle,
  Timer,
  PieChart as PieChartIcon,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { getSupabaseAdminClient, Profile } from '@parentpath/supabase-client';
import { SectionHeader } from '../components/ui';

const supabase = getSupabaseAdminClient();

type RecentUser = Pick<Profile, 'id' | 'email' | 'display_name' | 'created_at'>;

// ---------------------------------------------------------------------------
// Farben
// ---------------------------------------------------------------------------

/**
 * Kategoriale Palette fuer Anteils-Diagramme. Gegen Farbfehlsichtigkeit
 * geprueft (heller Untergrund #ffffff): schlechtestes *benachbartes* Paar
 * dE 11.3 unter simulierter Deuteranopie und 30.2 bei Normalsicht, alle sechs
 * Slots >= 3:1 Kontrast zum Untergrund.
 *
 * Geprueft ist die Nachbarschaft im Ring - inklusive des letzten Segments
 * gegen das erste, das sich im Tortendiagramm ebenfalls beruehrt. Ueber
 * *alle* Paare halten fuenf kategoriale Farben die Schwellen nicht (das ist
 * eine Eigenschaft von fuenf Farben, keine Frage der Auswahl); die
 * Reihenfolge sorgt deshalb dafuer, dass die schwachen Paare nie
 * aneinandergrenzen.
 *
 * Daraus folgt: Die Segmentreihenfolge muss fest an dieser Liste haengen und
 * darf NICHT nach Groesse sortiert werden - sonst landen die schwachen Paare
 * je nach Datenlage doch nebeneinander. Nicht umsortieren; beim Erweitern die
 * Ring-Nachbarschaft neu pruefen.
 */
const CATEGORICAL = ['#0d9488', '#7c3aed', '#db2777', '#d97706', '#2563eb'];

/**
 * Statusfarben der Cron-Uebersicht. Gruen/Rot ist unter Deuteranopie das
 * kritische Paar; diese beiden Stufen halten dE 8.6 (CVD) bzw. 32.0
 * (Normalsicht) und liegen beide ueber 3:1 Kontrast. Die Farbe traegt die
 * Bedeutung trotzdem nie allein: jede Zeile hat rechts eine Fehlerzahl im
 * Klartext und jeder Balken einen Tooltip.
 */
const CRON_COLOR = {
  ok: '#059669',
  error: '#dc2626',
  missing: '#cbd5e1',
  pending: '#f1f5f9',
} as const;

// ---------------------------------------------------------------------------
// Hilfsfunktionen
// ---------------------------------------------------------------------------

const formatNumber = (num: number) => num.toLocaleString('de-DE');

const formatPercent = (share: number) =>
  `${(share * 100).toLocaleString('de-DE', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} %`;

/** Trendwert mit Vorzeichen; unter 10 % mit einer Nachkommastelle. */
const formatSignedPercent = (value: number) => {
  const rounded = Math.abs(value).toLocaleString('de-DE', {
    maximumFractionDigits: Math.abs(value) < 10 ? 1 : 0,
  });
  return `${value >= 0 ? '+' : '\u2212'}${rounded} %`;
};

const formatDuration = (ms: number) =>
  ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${ms} ms`;

const formatTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

/**
 * Tagesschluessel als YYYY-MM-DD in Europe/Berlin.
 *
 * Feste Zeitzone statt Browserzeit, weil die Gegenseite fest ist: die
 * Tageszeilen in user_activity_days werden serverseitig in Europe/Berlin
 * geschnitten (Migration 167). Liefe die Achse in Browserzeit, verschoeben
 * sich fuer ein Panel ausserhalb der deutschen Zeitzone die Tageswerte um
 * einen Tag gegen die Registrierungen - eine Verschiebung, die man dem Chart
 * nicht ansieht. Fuer ein Panel in DE aendert sich dadurch nichts.
 */
const BERLIN_DAY_FORMAT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Berlin',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});
const dayKey = (d: Date) => BERLIN_DAY_FORMAT.format(d);

/** Achsenbeschriftung ebenfalls in Berliner Zeit, gleicher Grund. */
const BERLIN_LABEL_FORMAT = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  day: '2-digit',
  month: '2-digit',
});

/**
 * Anker eines Kalendertags: 12:00 UTC. Mittag, damit das Weiterzaehlen in
 * 24-Stunden-Schritten keine Zeitumstellung ueberspringt oder doppelt zaehlt
 * (UTC kennt keine, und 12:00 UTC liegt in Berlin immer am selben Tag).
 */
const dayAnchor = (key: string) => new Date(`${key}T12:00:00Z`);
const addDays = (d: Date, days: number) => new Date(d.getTime() + days * 86_400_000);

const formatRelativeDate = (dateString: string) => {
  const date = new Date(dateString);
  const diff = Date.now() - date.getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (hours < 1) return 'gerade eben';
  if (hours < 24) return `vor ${hours} Stunden`;
  if (days === 1) return 'gestern';
  return `vor ${days} Tagen`;
};

// ---------------------------------------------------------------------------
// KPI-Typen (Migration 165)
// ---------------------------------------------------------------------------

interface DashboardKpis {
  total_users: number;
  onboarding_pending: number;
  /**
   * Aus profiles.platform (Migration 167). null, solange noch fuer keinen
   * einzigen Nutzer eine Plattform erfasst ist -> Anzeige "—" statt einer
   * harten 0 waehrend des Rollouts.
   */
  platform_ios: number | null;
  platform_android: number | null;
  active_pregnancies: number;
  active_parenthoods: number;
  partner_connections: number;
  partner_connections_pregnancy: number;
  partner_connections_parenthood: number;
  partner_connections_other: number;
  premium_users: number;
  premium_by_source: Record<string, number>;
}

/**
 * Alle premium_source-Werte laut CHECK-Constraint (062), in fester Reihenfolge.
 * Die Farbe haengt an der Quelle, nicht an ihrem Rang: ueberholt Google Play
 * den App Store, behaelt jede Quelle trotzdem ihre Farbe.
 */
const PREMIUM_SOURCES: { key: string; label: string; color: string }[] = [
  // Diese drei kommen real vor. Als Dreiergruppe halten sie die Schwellen
  // ueber *alle* Paare (dE 12.5 CVD / 24.3 Normalsicht) - hier faellt je nach
  // Datenlage eine Quelle mit 0 weg, die Nachbarschaft im Ring ist also nicht
  // vorhersagbar und muss fuer jede Kombination stimmen.
  { key: 'apple', label: 'App Store', color: '#0d9488' },
  { key: 'google', label: 'Google Play', color: '#7c3aed' },
  { key: 'stripe', label: 'Stripe (Web)', color: '#d97706' },
  // Diese drei sind Rueckfallebene (kommen laut Datenbestand nicht vor). Ab
  // vier Kategorien ist Sicherheit ueber alle Paare nicht mehr erreichbar;
  // die Zuordnung traegt dann die Legende mit Name und Prozentwert.
  { key: 'beta_code', label: 'Beta-Code', color: '#2563eb' },
  { key: 'promo_code', label: 'Promo-Code', color: '#db2777' },
  { key: 'unbekannt', label: 'Ohne Quelle', color: '#65a30d' },
];

// ---------------------------------------------------------------------------
// Bausteine
// ---------------------------------------------------------------------------

interface KpiCardProps {
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  label: string;
  value: string | number;
  children?: React.ReactNode;
}

/**
 * KPI-Kachel: Label oben, Zahl darunter, optional kleine Zusatzzeilen.
 * Bewusst lokal statt der geteilten StatCard - die wird von vier weiteren
 * Admin-Seiten mit der umgekehrten Anordnung (Zahl oben) verwendet.
 */
function KpiCard({ icon: Icon, iconBg, iconColor, label, value, children }: KpiCardProps) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 ${iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-6 h-6 ${iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-slate-600 break-words">{label}</div>
          <div className="text-2xl font-bold text-slate-900 leading-tight mt-0.5 break-words">
            {value}
          </div>
          {children && <div className="mt-2 space-y-1 min-w-0">{children}</div>}
        </div>
      </div>
    </div>
  );
}

/**
 * Hintergrund einer Alert-Kachel nach Anzahl offener Vorgaenge:
 * nichts offen -> Markenfarbe Teal, 1-9 -> Markenfarbe Orange, ab 10 -> Rot.
 *
 * Zum Kontrast: weisser Text erreicht auf pp-teal 3.75:1 und auf pp-orange
 * 2.15:1. Fuer die grosse Zahl reicht das auf Teal (Grenze 3:1 fuer grossen
 * Text), fuer die kleine Beschreibung auf beiden Flaechen nicht (Grenze
 * 4.5:1). Dunklere Stufen wuerden das loesen (z. B. #0f766e mit 5.47:1 und
 * #b45309 mit 4.68:1), weichen dann aber von den Markenfarben ab - das ist
 * eine Design-Entscheidung, keine technische.
 * Rot ist bewusst #dc2626 (4.83:1): es ist der einzige Ton hier, der jede
 * Textgroesse traegt, und es ist dasselbe Rot wie bei den Cron-Fehlern.
 */
const alertTone = (count: number) => {
  if (count === 0) return 'bg-pp-teal';
  if (count < 10) return 'bg-pp-orange';
  return 'bg-red-600';
};

interface AlertItem {
  count: number;
  singular: string;
  plural: string;
  to: string;
}

function AlertTile({ item }: { item: AlertItem }) {
  return (
    <Link
      to={item.to}
      className={`flex items-center gap-4 p-4 rounded-lg text-white transition-opacity hover:opacity-90 ${alertTone(item.count)}`}
    >
      <span className="text-3xl font-bold leading-none tabular-nums flex-shrink-0">
        {formatNumber(item.count)}
      </span>
      <span className="text-sm leading-snug break-words">
        {item.count === 1 ? item.singular : item.plural}
      </span>
    </Link>
  );
}

interface ShareDatum {
  name: string;
  value: number;
  color: string;
}

/**
 * Tortendiagramm fuer Anteile. Zeigt ausschliesslich Prozente - absolute
 * Werte stehen, wo sie gebraucht werden, als Kennzahl ueber dem Diagramm.
 *
 * Segmente sind durch eine 2px-Luecke in Untergrundfarbe getrennt (kein
 * Rahmen). Direkt beschriftet werden nur Segmente ab 8 %, damit sich die
 * Beschriftungen kleiner Segmente nicht ueberlagern; die Legende darunter
 * fuehrt jeden Eintrag mit Namen und Prozentwert auf, sodass die Farbe die
 * Zuordnung nie allein tragen muss.
 */
function SharePie({ data, emptyLabel }: { data: ShareDatum[]; emptyLabel: string }) {
  const total = data.reduce((sum, d) => sum + d.value, 0);

  if (total === 0) {
    return (
      <div className="flex items-center justify-center h-[240px]">
        <p className="text-sm text-slate-500">{emptyLabel}</p>
      </div>
    );
  }

  const visible = data.filter((d) => d.value > 0);

  return (
    <div>
      <div className="h-[240px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={visible}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={52}
              outerRadius={92}
              paddingAngle={2}
              stroke="#ffffff"
              strokeWidth={2}
              labelLine={false}
              label={({ percent, x, y, textAnchor }) => {
                if (!percent || percent < 0.08) return null;
                return (
                  <text
                    x={x}
                    y={y}
                    textAnchor={textAnchor}
                    dominantBaseline="central"
                    fill="#334155"
                    fontSize={12}
                    fontWeight={600}
                  >
                    {formatPercent(percent)}
                  </text>
                );
              }}
            >
              {visible.map((entry) => (
                <Cell key={entry.name} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: number) => formatPercent(value / total)}
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '12px',
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Legende: Name + Prozent, damit die Zuordnung nicht an der Farbe haengt */}
      {/* Legende in Segmentreihenfolge - einspaltig, damit die Zuordnung
          Legende <-> Segment eindeutig bleibt. */}
      <div className="mt-4 space-y-1.5">
        {data.map((entry) => (
          <div key={entry.name} className="flex items-center justify-between gap-2 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <span
                className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-xs text-slate-600 truncate">{entry.name}</span>
            </div>
            <span className="text-xs font-medium text-slate-700 tabular-nums flex-shrink-0">
              {formatPercent(entry.value / total)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cron-Uebersicht
// ---------------------------------------------------------------------------

const CRON_WINDOW_HOURS = 6;

/**
 * Mindestbreite eines Balkens und Abstand dazwischen. Unterhalb dieser Breite
 * verschwinden die Balken auf schmalen Viewports; die Zeile scrollt dann
 * horizontal, statt die Anzeige unlesbar zu machen.
 */
const CRON_BAR_MIN_WIDTH = 6;
const CRON_BAR_GAP = 2;

/** Inhaltsbreite einer Zeile mit `slots` Balken bei Mindestbreite. */
const cronRowMinWidth = (slots: number) =>
  slots * CRON_BAR_MIN_WIDTH + Math.max(0, slots - 1) * CRON_BAR_GAP;

/**
 * Fahrplan der Jobs aus apps/api-server/src/index.ts. Wird gebraucht, um
 * ausgefallene Laeufe als Luecke sichtbar zu machen - ohne Soll-Intervall
 * laesst sich ein fehlender Lauf nicht von "laeuft eben seltener" trennen.
 * Neue Jobs hier ergaenzen; unbekannte Jobs werden ohne Luecken dargestellt.
 */
const JOB_INTERVAL_MIN: Record<string, number> = {
  'medication-reminders': 5,
  'notification-batching': 5,
  'sleep-plan': 5,
  'appointment-reminders': 15,
  'checklist-notifications': 15,
  'contraction-stats': 15,
  'scheduled-broadcasts': 15,
};

/** Taeglich laufende Jobs: im 6h-Fenster hoechstens ein Lauf, keine Luecken. */
const DAILY_JOBS = new Set(['daily-notifications', 'sleep-plan-morning-reminder']);

interface CronLogRow {
  id: string;
  job_name: string;
  started_at: string;
  finished_at: string;
  duration_ms: number;
  status: 'ok' | 'error';
  error_message: string | null;
}

interface CronSlot {
  status: 'ok' | 'error' | 'missing' | 'pending';
  slotStart: number;
  finishedAt?: string;
  durationMs?: number;
  errorMessage?: string | null;
}

/** Raster ueber das Zeitfenster: ein Slot je erwartetem Lauf. */
function buildIntervalSlots(
  logs: CronLogRow[],
  intervalMin: number,
  windowStart: number,
  now: number,
): CronSlot[] {
  const interval = intervalMin * 60_000;
  const count = Math.max(1, Math.ceil((now - windowStart) / interval));

  const slots: CronSlot[] = Array.from({ length: count }, (_, i) => ({
    status: 'missing',
    slotStart: windowStart + i * interval,
  }));

  for (const log of logs) {
    const index = Math.floor((new Date(log.finished_at).getTime() - windowStart) / interval);
    if (index < 0 || index >= count) continue;

    // Faellt mehr als ein Lauf in einen Slot, gewinnt der Fehler.
    if (slots[index].status === 'error') continue;

    slots[index] = {
      status: log.status,
      slotStart: slots[index].slotStart,
      finishedAt: log.finished_at,
      durationMs: log.duration_ms,
      errorMessage: log.error_message,
    };
  }

  // Der letzte Slot laeuft noch - ein fehlender Lauf ist hier kein Ausfall.
  const last = slots[count - 1];
  if (last.status === 'missing') last.status = 'pending';

  return slots;
}

/** Nur die tatsaechlichen Laeufe - fuer taegliche und unbekannte Jobs. */
function buildRunSlots(logs: CronLogRow[]): CronSlot[] {
  return [...logs]
    .sort((a, b) => new Date(a.finished_at).getTime() - new Date(b.finished_at).getTime())
    .map((log) => ({
      status: log.status,
      slotStart: new Date(log.finished_at).getTime(),
      finishedAt: log.finished_at,
      durationMs: log.duration_ms,
      errorMessage: log.error_message,
    }));
}

/**
 * Eine Job-Zeile: Label, Balkenkette, Status.
 *
 * Ab `xl` (1280px) stehen die drei nebeneinander, darunter bricht die
 * Balkenkette in eine eigene Zeile unter Label und Status um.
 *
 * Der Umbruchpunkt ist nicht gegriffen, sondern der Punkt, ab dem die
 * dichteste Zeile nebeneinander vollstaendig passt: 5-Minuten-Jobs ergeben im
 * 6-Stunden-Fenster 72 Slots, also 72 * 6px + 71 * 2px = 574px Inhalt.
 * Nebeneinander gehen davor 192px Label + 112px Status + 2 * 16px Abstand =
 * 336px ab, dazu 48px Kartenpolster, 64px Seitenpolster und 256px Sidebar -
 * macht 1278px Viewport fuer eine scrollfreie Kette. Darunter bliebe der
 * Kette weniger als die Haelfte ihrer Breite; umgebrochen stehen ihr
 * stattdessen die vollen ~940px der Karte zur Verfuegung, und sie passt schon
 * ab rund 1010px Viewport ohne Scrollen hinein.
 */
function CronJobRow({
  jobName,
  slots,
  fill,
  contentMinWidth,
  registerScroller,
  onScrollSync,
}: {
  jobName: string;
  slots: CronSlot[];
  fill: boolean;
  /** Fuer alle Zeilen gleich - siehe cronContentMinWidth. */
  contentMinWidth: number;
  registerScroller: (element: HTMLDivElement | null) => void;
  onScrollSync: (source: HTMLDivElement) => void;
}) {
  // Balken haben eine Mindestbreite; reicht der Platz nicht, scrollt die Zeile
  // horizontal. Der Tooltip liegt bewusst AUSSERHALB des Scroll-Containers:
  // overflow-x macht auch die vertikale Achse zum Clipping-Bereich, ein
  // Tooltip ueber den Balken wuerde darin abgeschnitten. Seine Position wird
  // deshalb beim Hovern in Pixeln relativ zur Zeile gemessen.
  const [hovered, setHovered] = useState<{ index: number; x: number } | null>(null);
  const rowRef = useRef<HTMLDivElement>(null);

  const errors = slots.filter((s) => s.status === 'error').length;
  const runs = slots.filter((s) => s.status === 'ok' || s.status === 'error').length;
  const gaps = slots.filter((s) => s.status === 'missing').length;
  const active = hovered ? slots[hovered.index] : null;

  return (
    // `order` haelt Label und Status auch im umgebrochenen Zustand in einer
    // Zeile; die Kette rutscht als `w-full` darunter. Ihr Abstand zum Label
    // (gap-y-2 = 8px) ist bewusst kleiner als der zwischen den Job-Zeilen
    // (space-y-5 = 20px), damit die Gruppierung erkennbar bleibt.
    <div className="flex flex-wrap xl:flex-nowrap items-center gap-x-3 sm:gap-x-4 gap-y-2">
      <div className="order-1 flex-1 min-w-0 xl:flex-none xl:w-48 xl:flex-shrink-0">
        <div className="font-mono text-xs text-slate-800 truncate">{jobName}</div>
        <div className="text-[11px] text-slate-400">{runs} Läufe</div>
      </div>

      <div ref={rowRef} className="order-3 w-full xl:order-2 xl:w-auto xl:flex-1 min-w-0 relative">
        <div
          ref={registerScroller}
          className="overflow-x-auto"
          onScroll={(event) => {
            // Beim Scrollen wandert der Balken unter dem Zeiger weg - der
            // gemessene Tooltip wuerde sonst an der alten Stelle stehen bleiben.
            setHovered(null);
            onScrollSync(event.currentTarget);
          }}
        >
          <div className="flex items-end" style={{ gap: `${CRON_BAR_GAP}px`, minWidth: `${contentMinWidth}px` }}>
            {slots.map((slot, index) => (
              <div
                key={slot.slotStart}
                onMouseEnter={(event) => {
                  const bar = event.currentTarget.getBoundingClientRect();
                  const row = rowRef.current?.getBoundingClientRect();
                  setHovered({
                    index,
                    x: row ? bar.left - row.left + bar.width / 2 : 0,
                  });
                }}
                onMouseLeave={() => setHovered(null)}
                className={`${fill ? 'flex-1' : 'flex-shrink-0 w-2.5'} h-8 rounded-[2px] cursor-default transition-opacity ${
                  hovered && hovered.index !== index ? 'opacity-60' : ''
                }`}
                style={{
                  backgroundColor: CRON_COLOR[slot.status],
                  minWidth: `${CRON_BAR_MIN_WIDTH}px`,
                }}
              />
            ))}
          </div>
        </div>

        {active && hovered && (
          <div
            className="absolute bottom-full mb-2 z-20 -translate-x-1/2 pointer-events-none"
            style={{ left: `${hovered.x}px` }}
          >
            <div className="bg-slate-900 text-white text-[11px] rounded-lg px-3 py-2 shadow-lg max-w-[280px]">
              {active.status === 'missing' && (
                <div className="whitespace-nowrap">
                  Kein Lauf um {formatTime(new Date(active.slotStart).toISOString())}
                </div>
              )}
              {active.status === 'pending' && (
                <div className="whitespace-nowrap">Lauf steht noch aus</div>
              )}
              {(active.status === 'ok' || active.status === 'error') && (
                <>
                  <div className="whitespace-nowrap font-medium">
                    {formatTime(active.finishedAt!)} · {formatDuration(active.durationMs ?? 0)}
                  </div>
                  <div className="whitespace-nowrap text-slate-300">
                    {active.status === 'ok' ? 'Erfolgreich' : 'Fehler'}
                  </div>
                  {active.status === 'error' && active.errorMessage && (
                    <div className="mt-1 text-red-200 break-words">{active.errorMessage}</div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="order-2 xl:order-3 w-20 sm:w-28 flex-shrink-0 text-right">
        {errors > 0 ? (
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-red-50 text-red-700 text-xs font-medium rounded-full">
            {errors} Fehler
          </span>
        ) : gaps > 0 ? (
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">
            {gaps} fehlen
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-full">
            OK
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Registrierungs-Chart
// ---------------------------------------------------------------------------

const RANGE_OPTIONS = [7, 30, 90] as const;
type RangeDays = (typeof RANGE_OPTIONS)[number];

interface RegistrationPoint {
  label: string;
  registrierungen: number;
  /** Gleicher Tagesversatz im vorherigen, gleich langen Zeitraum. */
  vorperiode: number | null;
  /**
   * Aktive Nutzer an diesem Tag (user_activity_days, Migration 167).
   * null = fuer diesen Tag liegt keine Messung vor, 0 = gemessene Null.
   */
  aktiveNutzer: number | null;
}

type RegistrationSeriesKey = 'registrierungen' | 'vorperiode' | 'aktiveNutzer';

/**
 * Die Serien des Registrierungs-Charts an einer Stelle.
 *
 * Gezeichnet und in der Legende normal dargestellt wird jede Serie, die in den
 * Daten mindestens einen Wert ungleich null hat - das entscheidet allein die
 * Datenlage, nicht der Serienname. Fuer 'aktiveNutzer' traegt das weiterhin:
 * die Serie liefert erst ab dem ersten gemessenen Tag Werte (Migration 167),
 * davor bleibt sie null. Ein Zeitraum komplett vor der Einfuehrung zeigt die
 * Linie deshalb gar nicht und graut sie in der Legende aus, statt eine
 * Nulllinie zu behaupten.
 *
 * Bernstein ist gegen das Teal der Registrierungen geprueft: dE 12.5 unter
 * simulierter Farbfehlsichtigkeit und 24.3 bei Normalsicht, beide ueber der
 * jeweiligen Schwelle.
 */
const REGISTRATION_SERIES: {
  key: RegistrationSeriesKey;
  label: string;
  color: string;
  dashed?: boolean;
}[] = [
  { key: 'registrierungen', label: 'Registrierungen', color: CATEGORICAL[0] },
  // Bewusst neutrales Grau und gestrichelt: die Vorperiode ist kein
  // eigenstaendiger Wert, sondern die Vergleichsbasis, und soll den aktuellen
  // Zeitraum nicht optisch verdraengen. Das Strichmuster traegt die
  // Unterscheidung zusaetzlich zur Farbe.
  { key: 'vorperiode', label: 'Vorperiode', color: '#64748b', dashed: true },
  { key: 'aktiveNutzer', label: 'Aktive Nutzer', color: CATEGORICAL[3] },
];

interface RegistrationChartData {
  points: RegistrationPoint[];
  total: number;
  previousTotal: number;
}

// ---------------------------------------------------------------------------

export default function DashboardPage() {
  const [rangeDays, setRangeDays] = useState<RangeDays>(30);

  // Alle KPI-Kacheln in einem Aufruf (Migration 165). Die Definitionen liegen
  // bewusst in der Datenbank, damit sie nicht von get_app_mode() abweichen.
  const { data: kpis } = useQuery<DashboardKpis>({
    queryKey: ['admin-dashboard-kpis'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('admin_dashboard_kpis');
      if (error) throw error;
      return data as unknown as DashboardKpis;
    },
  });

  // Laedt den gewaehlten Zeitraum UND den gleich langen davor, damit Trend und
  // Vergleichslinie aus derselben Abfrage kommen.
  const { data: registrationData } = useQuery<RegistrationChartData>({
    queryKey: ['registration-chart', rangeDays],
    queryFn: async () => {
      const today = dayAnchor(dayKey(new Date()));
      const start = addDays(today, -(rangeDays - 1));
      const compareStart = addDays(start, -rangeDays);

      // Einen Tag frueher greifen, als die Achse reicht: die Grenze wird als
      // Zeitpunkt gefiltert, einsortiert wird aber nach Berliner Kalendertag.
      // Ohne den Puffer fehlten je nach Zeitzone des Panels die fruehen
      // Stunden des ersten Tages. Zeilen ausserhalb der Achse fallen bei der
      // Zuordnung unten von selbst heraus.
      const fetchFrom = addDays(compareStart, -1);

      const [registrations, activity] = await Promise.all([
        supabase
          .from('profiles')
          .select('created_at')
          .gte('created_at', fetchFrom.toISOString())
          .order('created_at', { ascending: true }),
        // Aggregiert in der Datenbank (Migration 167) - die Rohzeilen waeren
        // Nutzer x Tage und muessten nur zum Zaehlen durch den Browser.
        // `as never` fuer die Argumente wie bei send_admin_broadcast
        // (BroadcastPage): Database erfuellt das GenericSchema von
        // postgrest-js nicht (es fehlen u.a. Views/CompositeTypes), das
        // Schema faellt deshalb auf `any` zurueck und die Argument-Inferenz
        // landet bei never. Betrifft jeden rpc-Aufruf MIT Argumenten am
        // typisierten Admin-Client.
        supabase.rpc('admin_active_users_by_day', {
          p_from: dayKey(start),
          p_to: dayKey(today),
        } as never),
      ]);

      if (registrations.error) throw registrations.error;
      if (activity.error) throw activity.error;

      const counts: Record<string, number> = {};
      for (const row of (registrations.data ?? []) as { created_at: string }[]) {
        const key = dayKey(new Date(row.created_at));
        counts[key] = (counts[key] ?? 0) + 1;
      }

      const activeRows = (activity.data ?? []) as {
        activity_day: string;
        active_users: number;
      }[];
      const activeByDay = new Map(activeRows.map((row) => [row.activity_day, row.active_users]));

      // Die RPC liefert nur Tage MIT Aktivitaet, aufsteigend sortiert. Der
      // erste davon trennt die beiden Sorten von Luecke: davor gibt es keine
      // Messung (Zeitraum vor Migration 167) und der Wert bleibt null, danach
      // ist eine Luecke eine echte Null. Sonst zoege die Linie ueber den
      // gesamten Zeitraum vor der Einfuehrung eine Nulllinie - die Behauptung
      // "null aktive Nutzer", wo in Wahrheit nichts gemessen wurde.
      const firstTrackedDay = activeRows[0]?.activity_day ?? null;

      const points: RegistrationPoint[] = [];
      let total = 0;
      let previousTotal = 0;

      for (let i = 0; i < rangeDays; i++) {
        const day = addDays(start, i);
        const compareDay = addDays(compareStart, i);
        const key = dayKey(day);

        const current = counts[key] ?? 0;
        const previous = counts[dayKey(compareDay)] ?? 0;
        total += current;
        previousTotal += previous;

        points.push({
          label: BERLIN_LABEL_FORMAT.format(day),
          registrierungen: current,
          vorperiode: previous,
          aktiveNutzer:
            firstTrackedDay !== null && key >= firstTrackedDay
              ? (activeByDay.get(key) ?? 0)
              : null,
        });
      }

      return { points, total, previousTotal };
    },
  });

  // Feature-Anteile der letzten 7 Tage. Nur Zaehlungen (head: true) - die
  // Zeilen selbst werden fuer die Anteile nicht gebraucht.
  const { data: featureShares } = useQuery<ShareDatum[]>({
    queryKey: ['feature-shares'],
    queryFn: async () => {
      const since = new Date();
      since.setDate(since.getDate() - 7);
      const iso = since.toISOString();

      const [swipes, ai, checklists, sleep, babyCare] = await Promise.all([
        supabase.from('name_swipes').select('id', { count: 'exact', head: true }).gte('created_at', iso),
        supabase.from('ai_conversations').select('id', { count: 'exact', head: true }).gte('created_at', iso),
        supabase
          .from('user_checklist_items')
          .select('id', { count: 'exact', head: true })
          .not('completed_at', 'is', null)
          .gte('completed_at', iso),
        supabase.from('sleep_sound_sessions').select('id', { count: 'exact', head: true }).gte('created_at', iso),
        supabase.from('baby_care_entries').select('id', { count: 'exact', head: true }).gte('created_at', iso),
      ]);

      // Feste Reihenfolge und feste Farbzuordnung je Feature (siehe CATEGORICAL).
      // Bewusst nicht nach Groesse sortiert.
      return [
        { name: 'Namens-Swipes', value: swipes.count ?? 0, color: CATEGORICAL[0] },
        { name: 'KI-Fragen', value: ai.count ?? 0, color: CATEGORICAL[1] },
        { name: 'Checklisten', value: checklists.count ?? 0, color: CATEGORICAL[2] },
        { name: 'Schlafklänge', value: sleep.count ?? 0, color: CATEGORICAL[3] },
        { name: 'Baby-Care', value: babyCare.count ?? 0, color: CATEGORICAL[4] },
      ];
    },
  });

  const { data: recentUsers } = useQuery<RecentUser[]>({
    queryKey: ['recent-users'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, display_name, created_at')
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data as RecentUser[];
    },
  });

  const { data: pendingSuggestions } = useQuery({
    queryKey: ['pending-suggestions'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('name_suggestions')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'pending');

      if (error) throw error;
      return count || 0;
    },
  });

  const { data: pendingProductSuggestions } = useQuery({
    queryKey: ['pending-product-suggestions'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('admin_product_suggestions')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'open');

      if (error) throw error;
      return count || 0;
    },
  });

  const { data: pendingIngredientReports } = useQuery({
    queryKey: ['pending-ingredient-reports'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('admin_ingredient_reports')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'open');

      if (error) throw error;
      return count || 0;
    },
  });

  const { data: pendingGdpr } = useQuery({
    queryKey: ['pending-gdpr'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('gdpr_requests')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'pending');

      if (error) throw error;
      return count || 0;
    },
  });

  const { data: pendingFeedback } = useQuery({
    queryKey: ['pending-feedback'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('feedback')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'new');

      if (error) throw error;
      return count || 0;
    },
  });

  // Cron-Laeufe im Fenster. Das Limit muss ueber der erwarteten Laufzahl
  // liegen (3 Jobs a 5 Min + 4 Jobs a 15 Min = 312 Laeufe in 6 Stunden),
  // sonst fehlt der aelteste Teil der Kette.
  const { data: cronLogs } = useQuery<CronLogRow[]>({
    queryKey: ['cron-execution-logs'],
    queryFn: async () => {
      const windowStart = new Date();
      windowStart.setHours(windowStart.getHours() - CRON_WINDOW_HOURS);

      const { data, error } = await supabase
        .from('cron_execution_log')
        .select('id, job_name, started_at, finished_at, duration_ms, status, error_message')
        .gte('finished_at', windowStart.toISOString())
        .order('finished_at', { ascending: true })
        .limit(1000);

      if (error) throw error;
      return (data ?? []) as CronLogRow[];
    },
    refetchInterval: 60_000,
  });

  // Zeilen der Cron-Uebersicht: alle konfigurierten Intervall-Jobs sind immer
  // dabei - ein komplett ausgefallener Job wuerde sonst einfach verschwinden,
  // statt als graue Zeile aufzufallen.
  // Alle Cron-Zeilen teilen sich eine Zeitachse. Scrollten sie unabhaengig
  // voneinander, stuenden untereinander verschiedene Zeitraeume - und die
  // Beschriftung "vor 6 Stunden / jetzt" waere fuer die meisten Zeilen falsch.
  // Deshalb wird die Scrollposition auf alle Zeilen gespiegelt.
  //
  // Das traegt auch im umgebrochenen Layout: dort ist jeder Scroll-Container
  // die volle Kartenbreite, die Inhaltsbreite ist ohnehin fuer alle Zeilen
  // gleich (cronContentMinWidth). Alle Zeilen haben also dieselbe maximale
  // Scrollweite, und ein Wechsel des Umbruchs kappt scrollLeft ueberall
  // identisch - die Zeilen bleiben synchron, ohne dass hier etwas passieren
  // muss.
  const cronScrollers = useRef<(HTMLDivElement | null)[]>([]);

  const registerCronScroller = (index: number) => (element: HTMLDivElement | null) => {
    cronScrollers.current[index] = element;
  };

  const syncCronScroll = (source: HTMLDivElement) => {
    for (const scroller of cronScrollers.current) {
      // Nur bei echter Abweichung schreiben - sonst loesen die gespiegelten
      // Zuweisungen wieder scroll-Ereignisse aus und die Sache dreht sich.
      if (scroller && scroller !== source && Math.abs(scroller.scrollLeft - source.scrollLeft) > 0.5) {
        scroller.scrollLeft = source.scrollLeft;
      }
    }
  };

  const cronRows = (() => {
    if (!cronLogs) return [];

    const now = Date.now();
    const windowStart = now - CRON_WINDOW_HOURS * 60 * 60_000;

    const byJob = new Map<string, CronLogRow[]>();
    for (const log of cronLogs) {
      const list = byJob.get(log.job_name);
      if (list) list.push(log);
      else byJob.set(log.job_name, [log]);
    }

    const jobNames = new Set<string>([...Object.keys(JOB_INTERVAL_MIN), ...byJob.keys()]);

    const rows = [...jobNames].map((jobName) => {
      const logs = byJob.get(jobName) ?? [];
      const intervalMin = JOB_INTERVAL_MIN[jobName];
      const useGrid = intervalMin !== undefined && !DAILY_JOBS.has(jobName);

      const slots = useGrid
        ? buildIntervalSlots(logs, intervalMin, windowStart, now)
        : buildRunSlots(logs);

      return {
        jobName,
        slots,
        fill: useGrid,
        errors: slots.filter((s) => s.status === 'error').length,
        gaps: slots.filter((s) => s.status === 'missing').length,
      };
    });

    // Auffaelliges nach oben: erst Fehler, dann Luecken, dann alphabetisch.
    return rows
      .filter((row) => row.slots.length > 0)
      .sort((a, b) => {
        if (a.errors !== b.errors) return b.errors - a.errors;
        if (a.gaps !== b.gaps) return b.gaps - a.gaps;
        return a.jobName.localeCompare(b.jobName);
      });
  })();

  /**
   * Alle Zeilen bekommen dieselbe Inhaltsbreite - abgeleitet aus der dichtesten
   * Zeile. Damit bleibt "Zeilen gleich breit, unterschiedlich dicht" auch dann
   * erhalten, wenn die Mindestbreite der Balken greift, und eine gemeinsame
   * Scrollposition zeigt in jeder Zeile denselben Zeitausschnitt.
   */
  const cronContentMinWidth = cronRowMinWidth(
    cronRows.reduce((max, row) => Math.max(max, row.slots.length), 0),
  );

  const premiumShares: ShareDatum[] = (() => {
    const bySource = kpis?.premium_by_source ?? {};
    const known = PREMIUM_SOURCES.map((source) => ({
      name: source.label,
      value: bySource[source.key] ?? 0,
      color: source.color,
    }));
    // Unerwartete Werte trotzdem ausweisen, damit die Prozente 100 % ergeben.
    const extra = Object.entries(bySource)
      .filter(([key]) => !PREMIUM_SOURCES.some((source) => source.key === key))
      .map(([key, count]) => ({
        name: key,
        value: count,
        color: '#65a30d',
      }));
    return [...known, ...extra].filter((entry) => entry.value > 0);
  })();

  // DSGVO und Feedback haben jetzt ebenfalls eine Verlinkung (Routen aus App.tsx).
  const alertItems: AlertItem[] = [
    {
      count: pendingSuggestions ?? 0,
      singular: 'Community-Namensvorschlag',
      plural: 'Community-Namensvorschläge',
      to: '/names/suggestions',
    },
    {
      count: pendingProductSuggestions ?? 0,
      singular: 'Produkt-Vorschlag',
      plural: 'Produkt-Vorschläge',
      to: '/features/product-check/suggestions',
    },
    {
      count: pendingIngredientReports ?? 0,
      singular: 'Inhaltsstoff-Meldung',
      plural: 'Inhaltsstoff-Meldungen',
      to: '/features/product-check/suggestions',
    },
    {
      count: pendingGdpr ?? 0,
      singular: 'offene DSGVO-Anfrage',
      plural: 'offene DSGVO-Anfragen',
      to: '/support/gdpr',
    },
    {
      count: pendingFeedback ?? 0,
      singular: 'neue Feedback-Nachricht',
      plural: 'neue Feedback-Nachrichten',
      to: '/support/feedback',
    },
  ];

  const connectionRate = kpis
    ? Math.round((kpis.partner_connections / Math.max(kpis.active_pregnancies, 1)) * 100)
    : 0;

  /**
   * Eine Serie wird nur gezeichnet, wenn sie ueberhaupt Werte hat. Eine Linie
   * auf 0 waere die Behauptung "null aktive Nutzer" - eine Aussage, die wir
   * ohne Daten nicht treffen koennen. Die Pruefung laeuft ueber die Daten, ist
   * also fuer jede Serie gleich und braucht keine Sonderfaelle je Name.
   */
  const seriesHasData = (key: RegistrationSeriesKey) =>
    registrationData?.points.some((point) => point[key] != null) ?? false;

  // Trend gegenueber dem vorherigen gleich langen Zeitraum. Ohne Basis (0
  // Registrierungen davor) gibt es keinen sinnvollen Prozentwert.
  const registrationTrend = (() => {
    if (!registrationData || registrationData.previousTotal === 0) return null;
    const { total, previousTotal } = registrationData;
    return ((total - previousTotal) / previousTotal) * 100;
  })();

  return (
    <div className="space-y-6">
      {/* KPI-Kacheln */}
      {/* 4 Spalten erst ab xl - bei lg (1024px) wurden die Kacheln mit Icon
          und Unterzeilen zu eng. Dazwischen immer 2 Spalten, ganz schmal 1. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard
          icon={Users}
          iconBg="bg-teal-100"
          iconColor="text-teal-600"
          label="Gesamtnutzer"
          value={formatNumber(kpis?.total_users ?? 0)}
        >
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
            <span className="flex items-center gap-1.5">
              <Apple className="w-3.5 h-3.5" />
              {kpis?.platform_ios != null ? formatNumber(kpis.platform_ios) : '—'}
            </span>
            <span className="flex items-center gap-1.5">
              <Bot className="w-3.5 h-3.5" />
              {kpis?.platform_android != null ? formatNumber(kpis.platform_android) : '—'}
            </span>
          </div>
          <div className="text-xs text-slate-400 break-words">
            {formatNumber(kpis?.onboarding_pending ?? 0)} ohne abgeschlossenes Onboarding
          </div>
        </KpiCard>

        <KpiCard
          icon={Heart}
          iconBg="bg-rose-100"
          iconColor="text-rose-600"
          label="Aktive Schwangerschaften"
          value={formatNumber(kpis?.active_pregnancies ?? 0)}
        />

        <KpiCard
          icon={Baby}
          iconBg="bg-amber-100"
          iconColor="text-amber-600"
          label="Aktive Elternschaften"
          value={formatNumber(kpis?.active_parenthoods ?? 0)}
        />

        <KpiCard
          icon={LinkIcon}
          iconBg="bg-blue-100"
          iconColor="text-blue-600"
          label="Partner-Connections"
          value={`${formatNumber(kpis?.partner_connections ?? 0)} (${connectionRate} %)`}
        >
          <div className="text-xs text-slate-400 break-words">
            {formatNumber(kpis?.partner_connections_pregnancy ?? 0)} Schwangerschaft ·{' '}
            {formatNumber(kpis?.partner_connections_parenthood ?? 0)} Elternschaft
          </div>
        </KpiCard>
      </div>

      {/* Registrierungen */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <SectionHeader
          icon={Users}
          title="Registrierungen"
          action={
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
              {RANGE_OPTIONS.map((days) => (
                <button
                  key={days}
                  onClick={() => setRangeDays(days)}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${
                    rangeDays === days
                      ? 'bg-white text-slate-900 shadow-sm font-medium'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {days} Tage
                </button>
              ))}
            </div>
          }
        />

        {/* Gesamtzahl im gewaehlten Zeitraum und Trend zur Vorperiode */}
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-4">
          <span className="text-3xl font-bold text-slate-900">
            {formatNumber(registrationData?.total ?? 0)}
          </span>
          <span className="text-sm text-slate-500">in {rangeDays} Tagen</span>
          {registrationTrend !== null ? (
            <span
              className={`inline-flex items-center gap-1 text-sm font-medium ${
                registrationTrend >= 0 ? 'text-emerald-700' : 'text-red-600'
              }`}
            >
              {/* Pfeil zusaetzlich zur Farbe - die Richtung haengt nicht an
                  der Farbe allein. */}
              <span aria-hidden="true">{registrationTrend >= 0 ? '\u2191' : '\u2193'}</span>
              {formatSignedPercent(registrationTrend)} ggü. Vorperiode
            </span>
          ) : (
            registrationData && (
              <span className="text-sm text-slate-400">keine Vergleichsdaten</span>
            )
          )}
        </div>

        <div className="h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={registrationData?.points ?? []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} tickLine={false} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 12 }} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                }}
              />
              {/* Alle Serien mit Daten - welche das sind, entscheidet allein
                  die Datenlage. Alle zaehlen Personen pro Tag und teilen sich
                  deshalb dieselbe Achse. */}
              {REGISTRATION_SERIES.filter((series) => seriesHasData(series.key)).map((series) => (
                <Line
                  key={series.key}
                  type="monotone"
                  dataKey={series.key}
                  name={series.label}
                  stroke={series.color}
                  strokeWidth={2}
                  strokeDasharray={series.dashed ? '5 4' : undefined}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-4">
          {REGISTRATION_SERIES.map((series) => {
            const pending = !seriesHasData(series.key);
            const color = pending ? '#e2e8f0' : series.color;
            return (
              <div key={series.key} className="flex items-center gap-2">
                {series.dashed ? (
                  <span
                    className="w-4 h-0.5"
                    style={{
                      backgroundImage: `repeating-linear-gradient(to right, ${color} 0 5px, transparent 5px 9px)`,
                    }}
                  />
                ) : (
                  <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: color }} />
                )}
                <span className={`text-sm ${pending ? 'text-slate-400' : 'text-slate-600'}`}>
                  {series.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Feature-Nutzung + Premium */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <SectionHeader icon={PieChartIcon} title="Feature-Nutzung (letzte 7 Tage)" />
          <SharePie
            data={featureShares ?? []}
            emptyLabel="Keine Feature-Nutzung in den letzten 7 Tagen"
          />
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <SectionHeader icon={Crown} title="Premium-Nutzer" />
          <div className="mb-2">
            <div className="text-3xl font-bold text-slate-900">
              {formatNumber(kpis?.premium_users ?? 0)}
            </div>
            <div className="text-sm text-slate-500">Premium-Nutzer gesamt</div>
          </div>
          <SharePie data={premiumShares} emptyLabel="Keine Premium-Nutzer" />
        </div>
      </div>

      {/* Alerts & neueste Registrierungen */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <SectionHeader icon={AlertCircle} title="Alerts & Aktionen" />
          <div className="space-y-3">
            {alertItems.map((item) => (
              <AlertTile key={item.to + item.plural} item={item} />
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <SectionHeader icon={Users} title="Neueste Registrierungen" />
          <div className="max-h-[400px] overflow-y-auto space-y-1.5">
            {recentUsers?.map((user) => (
              <div key={user.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 bg-slate-200 rounded-full flex items-center justify-center text-slate-600 font-medium text-xs">
                    {user.display_name?.charAt(0).toUpperCase() || user.email.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="text-xs font-medium text-slate-900">
                      {user.display_name || 'Unbenannt'}
                    </div>
                    <div className="text-[11px] text-slate-500">{user.email}</div>
                  </div>
                </div>
                <span className="text-[11px] text-slate-500 whitespace-nowrap ml-2">
                  {formatRelativeDate(user.created_at)}
                </span>
              </div>
            ))}
            {(!recentUsers || recentUsers.length === 0) && (
              <p className="text-sm text-slate-500 text-center py-4">Keine Registrierungen gefunden</p>
            )}
          </div>
        </div>
      </div>

      {/* Cron-Jobs */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <SectionHeader
          icon={Timer}
          title={`Cron-Jobs (letzte ${CRON_WINDOW_HOURS} Stunden)`}
          action={
            <div className="flex items-center gap-4">
              {[
                { label: 'OK', color: CRON_COLOR.ok },
                { label: 'Fehler', color: CRON_COLOR.error },
                { label: 'Kein Lauf', color: CRON_COLOR.missing },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: item.color }} />
                  <span className="text-xs text-slate-600">{item.label}</span>
                </div>
              ))}
            </div>
          }
        />

        {cronRows.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-6">
            Keine Cron-Ausführungen in den letzten {CRON_WINDOW_HOURS} Stunden
          </p>
        ) : (
          <>
            <div className="space-y-5 xl:space-y-3">
              {cronRows.map((row, index) => (
                <CronJobRow
                  key={row.jobName}
                  jobName={row.jobName}
                  slots={row.slots}
                  fill={row.fill}
                  contentMinWidth={cronContentMinWidth}
                  registerScroller={registerCronScroller(index)}
                  onScrollSync={syncCronScroll}
                />
              ))}
            </div>
            {/* Umgebrochen nimmt die Kette die volle Breite ein - dann darf
                die Zeitachse nicht mehr auf Label und Status eingerueckt sein. */}
            <div className="flex items-center justify-between mt-3 xl:pl-52 xl:pr-28">
              <span className="text-[11px] text-slate-400">vor {CRON_WINDOW_HOURS} Stunden</span>
              <span className="text-[11px] text-slate-400">jetzt</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
